import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import type {
  MemphisAssetAtRisk,
  MemphisAssetOperationalStatus,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/**
 * Tokyo Bay earthquake demo. Every name, figure and quote here is fictional and
 * unclassified: the chemical complex and its operator do not exist, and no unit
 * strengths, readiness data or patient numbers are real.
 */

/** Stable id for the hardcoded Tokyo Bay earthquake inbox item. */
export const JAPAN_THREAT_NOTIFICATION_ID = 'demo-japan-tokyo-bay-earthquake';

export const JAPAN_ASSETS_LAYER_ID = 'japan-tokyo-bay-assets';
export const JAPAN_ASSETS_LAYER_TITLE = 'Tokyo Bay assets / 東京湾 重要施設';

export const JAPAN_PLUME_LAYER_ID = 'japan-chemical-plume';
export const JAPAN_PLUME_LAYER_TITLE =
  'Keihin chemical release / 京浜 化学物質漏えい';

export const JAPAN_INTENSITY_LAYER_ID = 'japan-shaking-intensity';
export const JAPAN_INTENSITY_LAYER_TITLE = 'Seismic intensity 6+ / 震度6強以上';

export type JapanPolygon = {
  id: string;
  name: string;
  description: string;
  resourceType: 'customPolygon' | 'exclusionZone';
  polygonColor: string;
  polygonOpacity: number;
  ring: number[][];
};

/** Upper 6 and above, centred on the epicentre in northern Tokyo Bay. */
export const JAPAN_INTENSITY_POLYGON: JapanPolygon = {
  id: 'japan-intensity-upper-6',
  name: 'Seismic intensity upper 6+ / 震度6強以上',
  description:
    'Area of JMA seismic intensity upper 6 and above from the M7.3 Tokyo Bay earthquake.',
  resourceType: 'customPolygon',
  polygonColor: '#dc2626',
  polygonOpacity: 0.18,
  ring: [
    [139.7, 35.45],
    [139.8, 35.4],
    [139.95, 35.42],
    [140.05, 35.5],
    [140.05, 35.62],
    [139.95, 35.7],
    [139.8, 35.7],
    [139.68, 35.62],
    [139.65, 35.52],
    [139.7, 35.45],
  ],
};

/** Runs downwind from the Kawasaki waterfront over the approaches to Haneda. */
export const JAPAN_PLUME_POLYGON: JapanPolygon = {
  id: 'japan-chemical-plume',
  name: 'Chlorine and ammonia plume / 塩素・アンモニア拡散域',
  description:
    'Modelled chlorine and ammonia plume from the Keihin Coastal Chemical Complex, drifting north-east over the Haneda approaches.',
  resourceType: 'customPolygon',
  polygonColor: '#eab308',
  polygonOpacity: 0.45,
  ring: [
    [139.755, 35.505],
    [139.78, 35.5],
    [139.82, 35.52],
    [139.86, 35.55],
    [139.87, 35.58],
    [139.84, 35.585],
    [139.8, 35.565],
    [139.77, 35.54],
    [139.75, 35.52],
    [139.755, 35.505],
  ],
};

export const JAPAN_PORT_EXCLUSION_POLYGON: JapanPolygon = {
  id: 'japan-port-exclusion',
  name: 'Kawasaki port exclusion zone / 川崎港 立入禁止区域',
  description:
    'Port of Kawasaki closed to vessel traffic while the release is contained.',
  resourceType: 'exclusionZone',
  polygonColor: '#7c3aed',
  polygonOpacity: 0.25,
  ring: [
    [139.7, 35.47],
    [139.79, 35.47],
    [139.79, 35.53],
    [139.7, 35.53],
    [139.7, 35.47],
  ],
};

export const JAPAN_PLUME_POLYGONS: JapanPolygon[] = [
  JAPAN_PLUME_POLYGON,
  JAPAN_PORT_EXCLUSION_POLYGON,
];

/** Tokyo Bay framing for the map: Yokota to Narita, Yokosuka to Kawasaki. */
export const JAPAN_MAP_EXTENT = {
  xmin: 139.25,
  ymin: 35.22,
  xmax: 140.5,
  ymax: 35.85,
};

const JAPAN_THREAT_SENT_AT = '2026-11-02T13:05:00.000Z';

export const JAPAN_EVENT_STARTED_AT = '2026-11-02T12:47:00.000Z';

export const JAPAN_THREAT_PROVENANCE =
  '12 Defense, Transport and Medical Assets Impacted by M7.3 Tokyo Bay Earthquake and Keihin Chemical Release';

export const JAPAN_EVENT_REGION = 'Tokyo Bay — Kanagawa and Tokyo';

/** The earthquake event; the inbox alert links here. */
export const JAPAN_ORG_EVENT_DEMO_KEY = 'japan-tokyo-bay-earthquake';
export const JAPAN_CHEMICAL_ORG_EVENT_DEMO_KEY =
  'japan-keihin-chemical-release';

export const JAPAN_RESOURCE_NOTES_PREFIX = 'japan-tokyo-bay:';

/** Response resources are not facilities, so they carry their own prefix. */
export const JAPAN_RESPONSE_RESOURCE_NOTES_PREFIX = 'japan-tokyo-bay-resource:';

export const JAPAN_ASSET_COA_NOTIFY_STORAGE_KEY =
  'japan-tokyo-bay-asset-coa-notify-v1';

export const JAPAN_EARTHQUAKE_RULE_NAME = 'Japan — earthquake';
export const JAPAN_CHEMICAL_RULE_NAME = 'Japan — chemical release';
export const JAPAN_PORT_AIRPORT_RULE_NAME =
  'Japan — port and airport disruption';
export const JAPAN_HADR_RULE_NAME = 'Japan — HADR';
export const JAPAN_NEO_RULE_NAME = 'Japan — NEO';

export const EARTHQUAKE_THREAT = 'Earthquake';
export const CHEMICAL_THREAT = 'Chemical Release';
export const PORT_AIRPORT_THREAT = 'Port and Airport Disruption';
export const HADR_THREAT = 'HADR';
export const NEO_THREAT = 'NEO';

export const JAPAN_THREATS = [
  EARTHQUAKE_THREAT,
  CHEMICAL_THREAT,
  PORT_AIRPORT_THREAT,
  HADR_THREAT,
  NEO_THREAT,
] as const;

export const JAPAN_THREAT_TO_RULE_NAME: Record<string, string> = {
  [EARTHQUAKE_THREAT]: JAPAN_EARTHQUAKE_RULE_NAME,
  [CHEMICAL_THREAT]: JAPAN_CHEMICAL_RULE_NAME,
  [PORT_AIRPORT_THREAT]: JAPAN_PORT_AIRPORT_RULE_NAME,
  [HADR_THREAT]: JAPAN_HADR_RULE_NAME,
  [NEO_THREAT]: JAPAN_NEO_RULE_NAME,
};

/** Threats each seeded event owns; together they cover JAPAN_THREATS once. */
export const JAPAN_EARTHQUAKE_EVENT_THREATS = [
  EARTHQUAKE_THREAT,
  PORT_AIRPORT_THREAT,
  HADR_THREAT,
  NEO_THREAT,
];
export const JAPAN_CHEMICAL_EVENT_THREATS = [CHEMICAL_THREAT];

export type JapanCountry = 'US' | 'JP';

export type JapanAgency = {
  country: JapanCountry;
  name: string;
};

const USFJ: JapanAgency = { country: 'US', name: 'USFJ J3' };
const USFJ_J4: JapanAgency = { country: 'US', name: 'USFJ J4' };
const US_EMBASSY: JapanAgency = { country: 'US', name: 'US Embassy Tokyo' };
const YOKOTA_EOC: JapanAgency = { country: 'US', name: 'Yokota Air Base' };
const FLEET_YOKOSUKA: JapanAgency = {
  country: 'US',
  name: 'Fleet Activities Yokosuka',
};
const JSDF: JapanAgency = { country: 'JP', name: 'JSDF Eastern Army' };
const KANAGAWA: JapanAgency = { country: 'JP', name: 'Kanagawa Prefecture' };
const KAWASAKI_CITY: JapanAgency = { country: 'JP', name: 'Kawasaki City' };
const KAWASAKI_FIRE: JapanAgency = {
  country: 'JP',
  name: 'Kawasaki City Fire Bureau',
};
const PORT_AUTHORITIES: JapanAgency = {
  country: 'JP',
  name: 'Kawasaki and Yokohama Port Authorities',
};
const RED_CROSS: JapanAgency = { country: 'JP', name: 'Japanese Red Cross' };

export function formatJapanAgency(agency: JapanAgency): string {
  return `[${agency.country}] ${agency.name}`;
}

function agencyLabel(agency: JapanAgency, task: string): string {
  return `${formatJapanAgency(agency)} — ${task}`;
}

export const JAPAN_DEFENSE_SURVEY_TASK =
  'Conduct survey of all defense critical infrastructure';

export const JAPAN_EARTHQUAKE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'japan-eq-accountability',
    kind: 'send_notification',
    label: agencyLabel(USFJ, 'Send personnel accountability notification'),
    description: formatJapanAgency(USFJ),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Personnel accountability — {eventName}',
      bodyTemplate:
        'Report personnel accountability for your installation following {eventName}. Report status in PRATUS.',
      emailSubject: 'Personnel accountability — {eventName}',
      emailMessage:
        'Report personnel and dependent accountability for your installation. Review impacted assets and Courses of Action in PRATUS.',
      appMessage:
        'Personnel accountability: {eventName} — report status for your installation.',
    },
  },
  {
    id: 'japan-eq-defense-survey',
    kind: 'org_action',
    label: agencyLabel(USFJ_J4, JAPAN_DEFENSE_SURVEY_TASK),
    description: `${formatJapanAgency(USFJ_J4)}. Rank each site by criticality and impact so the most critical facilities are restored first.`,
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-eq-structural',
    kind: 'org_action',
    label: agencyLabel(JSDF, 'Assess structural damage at ports and airfields'),
    description: formatJapanAgency(JSDF),
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-eq-survey-report',
    kind: 'send_notification',
    label: agencyLabel(USFJ_J4, 'Request survey results from every site'),
    description: formatJapanAgency(USFJ_J4),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Defense infrastructure survey — {eventName}',
      bodyTemplate:
        'Complete the defense critical infrastructure survey for your site and report operational status, damage and restoration priority in PRATUS.',
      emailSubject: 'Defense infrastructure survey — {eventName}',
      emailMessage:
        'Report operational status, structural damage and restoration priority for your site. Results feed the ranked asset list in PRATUS.',
      appMessage:
        'Defense infrastructure survey: {eventName} — report your site status.',
    },
  },
];

export const JAPAN_CHEMICAL_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'japan-chem-shelter',
    kind: 'send_notification',
    label: agencyLabel(
      KAWASAKI_CITY,
      'Send shelter-in-place advisory for Kawasaki and Ota wards'
    ),
    description: formatJapanAgency(KAWASAKI_CITY),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Shelter in place — {eventName}',
      bodyTemplate:
        'Chlorine and ammonia release from the Keihin waterfront. Residents and personnel in Kawasaki-ku and Ota-ku shelter indoors, close windows and switch off ventilation.',
      emailSubject: 'Shelter in place — {eventName}',
      emailMessage:
        'Shelter indoors in Kawasaki-ku and Ota-ku. Close windows and switch off ventilation until the all-clear.',
      appMessage:
        'Shelter in place: Kawasaki-ku and Ota-ku — chemical release from the Keihin waterfront.',
    },
  },
  {
    id: 'japan-chem-plume-extent',
    kind: 'org_action',
    label: agencyLabel(KAWASAKI_FIRE, 'Confirm plume extent and release rate'),
    description: formatJapanAgency(KAWASAKI_FIRE),
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-chem-decon',
    kind: 'org_action',
    label: agencyLabel(
      KAWASAKI_FIRE,
      'Stage hazmat decontamination at Kawasaki Municipal Hospital'
    ),
    description: formatJapanAgency(KAWASAKI_FIRE),
    orgAction: { status: 'Planned', poc: null },
  },
];

export const JAPAN_PORT_AIRPORT_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'japan-port-notify',
    kind: 'send_notification',
    label: agencyLabel(
      PORT_AUTHORITIES,
      'Notify port authorities and Haneda operations'
    ),
    description: formatJapanAgency(PORT_AUTHORITIES),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Port and airport disruption — {eventName}',
      bodyTemplate:
        'The Port of Kawasaki is closed and two Haneda runways are closed under the chemical plume. Confirm berth availability at Yokohama Honmoku and divert arrivals to Narita.',
      emailSubject: 'Port and airport disruption — {eventName}',
      emailMessage:
        'Port of Kawasaki closed; Haneda runways closed under the plume. Coordinate berth availability and arrival diversions.',
      appMessage:
        'Port of Kawasaki closed and Haneda runways closed — coordinate diversions.',
    },
  },
  {
    id: 'japan-port-divert',
    kind: 'org_action',
    label: agencyLabel(USFJ, 'Divert inbound military arrivals to Narita'),
    description: formatJapanAgency(USFJ),
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-port-honmoku',
    kind: 'org_action',
    label: agencyLabel(
      PORT_AUTHORITIES,
      'Assess berths at Yokohama Honmoku for relief shipping'
    ),
    description: formatJapanAgency(PORT_AUTHORITIES),
    orgAction: { status: 'Planned', poc: null },
  },
];

export const JAPAN_HADR_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'japan-hadr-coordinate',
    kind: 'org_action',
    label: agencyLabel(
      KANAGAWA,
      'Coordinate HADR tasking with JSDF and Kanagawa Prefecture'
    ),
    description: `${formatJapanAgency(KANAGAWA)} with ${formatJapanAgency(JSDF)}`,
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-hadr-stage-supplies',
    kind: 'org_action',
    label: agencyLabel(FLEET_YOKOSUKA, 'Stage relief supplies at Yokosuka'),
    description: formatJapanAgency(FLEET_YOKOSUKA),
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-hadr-medical-surge',
    kind: 'send_notification',
    label: agencyLabel(RED_CROSS, 'Send medical surge notification'),
    description: formatJapanAgency(RED_CROSS),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Medical surge — {eventName}',
      bodyTemplate:
        'Kawasaki Municipal Hospital is diverting. Route chemical exposure casualties to facilities with decontamination capacity and report bed status in PRATUS.',
      emailSubject: 'Medical surge — {eventName}',
      emailMessage:
        'Route chemical exposure casualties to facilities with decontamination capacity. Report bed and emergency room status in PRATUS.',
      appMessage:
        'Medical surge: {eventName} — route exposure casualties to decontamination-capable hospitals.',
    },
  },
];

export const JAPAN_NEO_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'japan-neo-warden',
    kind: 'send_notification',
    label: agencyLabel(US_EMBASSY, 'Send warden message to US citizens'),
    description: formatJapanAgency(US_EMBASSY),
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Message for US citizens — {eventName}',
      bodyTemplate:
        'US citizens in Kanagawa and southern Tokyo: follow local shelter-in-place guidance, avoid Kawasaki-ku and Ota-ku, and register for departure assistance.',
      emailSubject: 'Message for US citizens — {eventName}',
      emailMessage:
        'Follow local shelter-in-place guidance. Avoid Kawasaki-ku and Ota-ku. Register for departure assistance through the Embassy.',
      appMessage:
        'US citizens: follow local guidance and register for departure assistance.',
    },
  },
  {
    id: 'japan-neo-ecc',
    kind: 'org_action',
    label: agencyLabel(
      YOKOTA_EOC,
      'Stand up the evacuation control centre at Yokota'
    ),
    description: formatJapanAgency(YOKOTA_EOC),
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'japan-neo-airlift',
    kind: 'org_action',
    label: agencyLabel(
      USFJ,
      'Coordinate charter and military airlift through Yokota, Narita as alternate'
    ),
    description: formatJapanAgency(USFJ),
    orgAction: { status: 'Planned', poc: null },
  },
];

const CHECKLISTS_BY_THREAT: Record<string, OrgEventChecklistItemDef[]> = {
  [EARTHQUAKE_THREAT]: JAPAN_EARTHQUAKE_CHECKLIST_ITEMS,
  [CHEMICAL_THREAT]: JAPAN_CHEMICAL_CHECKLIST_ITEMS,
  [PORT_AIRPORT_THREAT]: JAPAN_PORT_AIRPORT_CHECKLIST_ITEMS,
  [HADR_THREAT]: JAPAN_HADR_CHECKLIST_ITEMS,
  [NEO_THREAT]: JAPAN_NEO_CHECKLIST_ITEMS,
};

const JAPAN_ORG_ACTION_LABELS = Object.values(CHECKLISTS_BY_THREAT)
  .flat()
  .filter((item) => item.kind === 'org_action')
  .map((item) => item.label);

export function isJapanOrgActionName(name: string | null | undefined): boolean {
  return !!name && JAPAN_ORG_ACTION_LABELS.includes(name);
}

export function japanChecklistsForThreats(
  threats: string[]
): Record<string, OrgEventChecklistItemDef[]> {
  const checklists: Record<string, OrgEventChecklistItemDef[]> = {};
  for (const threat of threats) {
    const items = CHECKLISTS_BY_THREAT[threat];
    if (items) checklists[threat] = items;
  }
  return checklists;
}

export function japanChecklistsForAutomationRules(threats: string[]): {
  automationRules: string[];
  automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]>;
} {
  const threatChecklists = japanChecklistsForThreats(threats);
  const automationRules: string[] = [];
  const automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]> =
    {};
  for (const threat of threats) {
    const ruleName = JAPAN_THREAT_TO_RULE_NAME[threat];
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    const items = threatChecklists[threat];
    if (items) automationRuleChecklists[ruleName] = items;
  }
  return { automationRules, automationRuleChecklists };
}

export type JapanCriticalityTier = 1 | 2 | 3;
export type JapanImpact = 'Low' | 'Medium' | 'High' | 'Severe';
export type JapanBedStatus = 'Open' | 'Limited' | 'Diverting';

export type JapanMedicalStatus = {
  beds: JapanBedStatus;
  decon: boolean;
  erNote: string;
  erNoteJa: string;
};

/**
 * Same base fields as the other facility demos so Japan shares their render
 * path, plus the ranking and bilingual fields the SITREP panel reads.
 */
export type JapanFacility = {
  id: string;
  name: string;
  nameJa: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
  country: JapanCountry;
  criticalityTier: JapanCriticalityTier;
  impact: JapanImpact;
  inImpactArea: boolean;
  threats: string[];
  risk: string;
  riskJa: string;
  operationalStatus: MemphisAssetOperationalStatus;
  rankNote: string;
  rankNoteJa: string;
  symbology: 'facility' | 'hospital' | 'hazardOrigin';
  medical?: JapanMedicalStatus;
};

export const JAPAN_FACILITIES: JapanFacility[] = [
  {
    id: 'japan-keihin-chemical',
    name: 'Keihin Coastal Chemical Complex',
    nameJa: '京浜臨海化学コンビナート',
    city: 'Kawasaki-ku',
    state: 'Kanagawa',
    lon: 139.765,
    lat: 35.515,
    country: 'JP',
    criticalityTier: 2,
    impact: 'Severe',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, CHEMICAL_THREAT, PORT_AIRPORT_THREAT],
    risk: 'Source of the release. Earthquake damage to two storage tanks is releasing chlorine and ammonia; the release rate is not yet confirmed.',
    riskJa:
      '漏えい源。地震により貯蔵タンク2基が損傷し、塩素とアンモニアが漏えいしている。漏えい量は未確認。',
    operationalStatus: 'Not Operational',
    rankNote: 'Source of the plume that closes Haneda and Kawasaki port.',
    rankNoteJa: '羽田空港と川崎港を閉鎖させている拡散の発生源。',
    symbology: 'hazardOrigin',
  },
  {
    id: 'japan-port-kawasaki',
    name: 'Port of Kawasaki',
    nameJa: '川崎港',
    city: 'Kawasaki-ku',
    state: 'Kanagawa',
    lon: 139.745,
    lat: 35.488,
    country: 'JP',
    criticalityTier: 2,
    impact: 'Severe',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, CHEMICAL_THREAT, PORT_AIRPORT_THREAT],
    risk: 'Closed to vessel traffic inside the exclusion zone. Quay walls show liquefaction damage and fuel berths are shut.',
    riskJa:
      '立入禁止区域内のため船舶の入港を停止。岸壁に液状化被害があり、燃料バースは閉鎖中。',
    operationalStatus: 'Not Operational',
    rankNote: 'Fuel and cargo port closed outright; no relief shipping.',
    rankNoteJa: '燃料・貨物港が完全閉鎖。救援物資の海上輸送が不可。',
    symbology: 'facility',
  },
  {
    id: 'japan-haneda',
    name: 'Haneda Airport',
    nameJa: '羽田空港',
    city: 'Ota',
    state: 'Tokyo',
    lon: 139.7798,
    lat: 35.5494,
    country: 'JP',
    criticalityTier: 1,
    impact: 'High',
    inImpactArea: true,
    threats: [
      EARTHQUAKE_THREAT,
      CHEMICAL_THREAT,
      PORT_AIRPORT_THREAT,
      NEO_THREAT,
    ],
    risk: 'Runways C and D are closed under the plume. Terminals are sheltering in place and arrivals are diverting to Narita.',
    riskJa:
      '拡散域の下にあるC・D滑走路を閉鎖。ターミナルは屋内退避中で、到着便は成田へ代替着陸。',
    operationalStatus: 'Partially Operational',
    rankNote:
      'Primary international gateway for the region; half its runways are lost.',
    rankNoteJa: '地域の主要な国際玄関口。滑走路の半数が使用不能。',
    symbology: 'facility',
  },
  {
    id: 'japan-fleet-yokosuka',
    name: 'Fleet Activities Yokosuka',
    nameJa: '横須賀海軍施設',
    city: 'Yokosuka',
    state: 'Kanagawa',
    lon: 139.665,
    lat: 35.288,
    country: 'US',
    criticalityTier: 1,
    impact: 'Medium',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, HADR_THREAT],
    risk: 'Pier damage under inspection. Base is operating and staging relief supplies for the HADR effort.',
    riskJa:
      '桟橋の損傷を点検中。基地は運用中で、HADR向けの救援物資を集積している。',
    operationalStatus: 'Partially Operational',
    rankNote: 'Key HADR staging base; damage limits pier throughput.',
    rankNoteJa: 'HADRの主要な集積拠点。損傷により桟橋の処理能力が低下。',
    symbology: 'facility',
  },
  {
    id: 'japan-naval-hospital-yokosuka',
    name: 'US Naval Hospital Yokosuka',
    nameJa: '米海軍横須賀病院',
    city: 'Yokosuka',
    state: 'Kanagawa',
    lon: 139.659,
    lat: 35.283,
    country: 'US',
    criticalityTier: 1,
    impact: 'Medium',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, HADR_THREAT],
    risk: 'Operating on generator power. Opening surge capacity for US personnel, dependents and referred casualties.',
    riskJa:
      '非常用発電で運用中。米軍関係者・家族および搬送患者向けに増床対応を開始。',
    operationalStatus: 'Partially Operational',
    rankNote: 'Only US medical facility in the bay with decontamination.',
    rankNoteJa: '湾内で除染能力を持つ唯一の米軍医療施設。',
    symbology: 'hospital',
    medical: {
      beds: 'Open',
      decon: true,
      erNote: 'Accepting referrals; surge beds opening.',
      erNoteJa: '搬送受け入れ中。増床を開始。',
    },
  },
  {
    id: 'japan-port-yokohama-honmoku',
    name: 'Port of Yokohama (Honmoku)',
    nameJa: '横浜港（本牧ふ頭）',
    city: 'Naka-ku',
    state: 'Kanagawa',
    lon: 139.676,
    lat: 35.427,
    country: 'JP',
    criticalityTier: 2,
    impact: 'Medium',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, PORT_AIRPORT_THREAT, HADR_THREAT],
    risk: 'Outside the plume. Several berths are under structural assessment; the rest can take relief shipping diverted from Kawasaki.',
    riskJa:
      '拡散域の外。一部のバースは構造点検中で、残りは川崎から振り替える救援船を受け入れ可能。',
    operationalStatus: 'Partially Operational',
    rankNote: 'Alternate port for relief shipping once berths are cleared.',
    rankNoteJa: 'バースの安全確認後、救援物資輸送の代替港となる。',
    symbology: 'facility',
  },
  {
    id: 'japan-narita',
    name: 'Narita Airport',
    nameJa: '成田国際空港',
    city: 'Narita',
    state: 'Chiba',
    lon: 140.3929,
    lat: 35.772,
    country: 'JP',
    criticalityTier: 1,
    impact: 'Low',
    inImpactArea: false,
    threats: [NEO_THREAT, PORT_AIRPORT_THREAT],
    risk: 'Undamaged. Receiving arrivals diverted from Haneda and serving as the alternate NEO departure point.',
    riskJa:
      '被害なし。羽田からの代替着陸便を受け入れ、NEOの代替出発拠点となる。',
    operationalStatus: 'Operational',
    rankNote: 'Alternate NEO gateway; capacity strained by diversions.',
    rankNoteJa: 'NEOの代替拠点。代替着陸便により処理能力がひっ迫。',
    symbology: 'facility',
  },
  {
    id: 'japan-yokota',
    name: 'Yokota Air Base',
    nameJa: '横田飛行場',
    city: 'Fussa',
    state: 'Tokyo',
    lon: 139.348,
    lat: 35.7485,
    country: 'US',
    criticalityTier: 1,
    impact: 'Low',
    inImpactArea: false,
    threats: [NEO_THREAT, HADR_THREAT],
    risk: 'Undamaged. Hub for the NEO airlift and the evacuation control centre.',
    riskJa: '被害なし。NEO空輸と退避統制センターの拠点。',
    operationalStatus: 'Operational',
    rankNote: 'NEO hub; any disruption here stops the evacuation.',
    rankNoteJa: 'NEOの拠点。ここが止まれば退避が停止する。',
    symbology: 'facility',
  },
  {
    id: 'japan-kawasaki-hospital',
    name: 'Kawasaki Municipal Hospital',
    nameJa: '川崎市立川崎病院',
    city: 'Kawasaki-ku',
    state: 'Kanagawa',
    lon: 139.7085,
    lat: 35.5288,
    country: 'JP',
    criticalityTier: 3,
    impact: 'High',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, CHEMICAL_THREAT, HADR_THREAT],
    risk: 'Nearest hospital to the release. Emergency room is diverting while it decontaminates chemical exposure casualties.',
    riskJa:
      '漏えい現場に最も近い病院。化学物質曝露患者の除染のため救急外来は受け入れ停止中。',
    operationalStatus: 'Partially Operational',
    rankNote: 'First receiver for exposure casualties; now diverting.',
    rankNoteJa: '曝露患者の最初の受け入れ先。現在は受け入れ停止中。',
    symbology: 'hospital',
    medical: {
      beds: 'Diverting',
      decon: true,
      erNote: 'Emergency room diverting; decontamination running.',
      erNoteJa: '救急外来は受け入れ停止。除染を実施中。',
    },
  },
  {
    id: 'japan-camp-zama',
    name: 'Camp Zama',
    nameJa: 'キャンプ座間',
    city: 'Zama',
    state: 'Kanagawa',
    lon: 139.398,
    lat: 35.497,
    country: 'US',
    criticalityTier: 2,
    impact: 'Low',
    inImpactArea: false,
    threats: [HADR_THREAT],
    risk: 'Undamaged. Coordinating US Army support to the HADR effort with JSDF.',
    riskJa: '被害なし。JSDFと連携し、HADRへの米陸軍の支援を調整中。',
    operationalStatus: 'Operational',
    rankNote: 'Supporting HADR coordination; no damage.',
    rankNoteJa: 'HADRの調整を支援。被害なし。',
    symbology: 'facility',
  },
  {
    id: 'japan-atsugi',
    name: 'Naval Air Facility Atsugi',
    nameJa: '厚木海軍飛行場',
    city: 'Ayase',
    state: 'Kanagawa',
    lon: 139.45,
    lat: 35.4547,
    country: 'US',
    criticalityTier: 2,
    impact: 'Low',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, HADR_THREAT],
    risk: 'Minor damage. Runway open; supporting helicopter operations for the HADR effort.',
    riskJa: '軽微な被害。滑走路は使用可能で、HADRのヘリコプター運用を支援。',
    operationalStatus: 'Operational',
    rankNote: 'Runway open; supports HADR helicopter operations.',
    rankNoteJa: '滑走路は使用可能。HADRのヘリ運用を支援。',
    symbology: 'facility',
  },
  {
    id: 'japan-ycu-hospital',
    name: 'Yokohama City University Hospital',
    nameJa: '横浜市立大学附属病院',
    city: 'Kanazawa-ku',
    state: 'Kanagawa',
    lon: 139.6248,
    lat: 35.338,
    country: 'JP',
    criticalityTier: 3,
    impact: 'Medium',
    inImpactArea: true,
    threats: [EARTHQUAKE_THREAT, HADR_THREAT],
    risk: 'Structural inspection under way. Taking trauma cases only until the inspection is complete.',
    riskJa: '構造点検を実施中。点検完了まで外傷患者のみ受け入れ。',
    operationalStatus: 'Partially Operational',
    rankNote: 'Trauma only until structural inspection clears.',
    rankNoteJa: '構造点検が完了するまで外傷患者のみ。',
    symbology: 'hospital',
    medical: {
      beds: 'Limited',
      decon: false,
      erNote: 'Trauma only; no decontamination.',
      erNoteJa: '外傷患者のみ。除染不可。',
    },
  },
];

const TIER_WEIGHT: Record<JapanCriticalityTier, number> = { 1: 3, 2: 2, 3: 1 };
const IMPACT_WEIGHT: Record<JapanImpact, number> = {
  Low: 1,
  Medium: 2,
  High: 3,
  Severe: 4,
};

/** Facilities exposed to at least one of these threats, in roster order. */
export function japanFacilitiesForThreats(threats: string[]): JapanFacility[] {
  return JAPAN_FACILITIES.filter((facility) =>
    facility.threats.some((threat) => threats.includes(threat))
  );
}

export type JapanOrgEventSpec = {
  demoKey: string;
  name: string;
  type: string;
  region: string;
  summary: string;
  thresholdDescription: string;
  resourcesCommitted: string;
  startedAt: string;
  lon: number;
  lat: number;
  threats: string[];
  checklistItems: OrgEventChecklistItemDef[];
  facilityIds: string[];
};

const JAPAN_EARTHQUAKE_FACILITIES = japanFacilitiesForThreats(
  JAPAN_EARTHQUAKE_EVENT_THREATS
);
const JAPAN_CHEMICAL_FACILITIES = japanFacilitiesForThreats(
  JAPAN_CHEMICAL_EVENT_THREATS
);
const JAPAN_KEIHIN_SOURCE = JAPAN_FACILITIES.find(
  (facility) => facility.id === 'japan-keihin-chemical'
)!;

export const JAPAN_EARTHQUAKE_EVENT: JapanOrgEventSpec = {
  demoKey: JAPAN_ORG_EVENT_DEMO_KEY,
  name: `Earthquake — M7.3 Tokyo Bay, ${JAPAN_EARTHQUAKE_FACILITIES.length} assets impacted`,
  type: EARTHQUAKE_THREAT,
  region: JAPAN_EVENT_REGION,
  summary:
    'A M7.3 earthquake in northern Tokyo Bay has caused upper-6 shaking across Yokohama, Kawasaki and Yokosuka. Defense, port and medical sites are reporting damage, Haneda and the Port of Kawasaki are closed, and HADR and NEO responses are under way with Japanese partners. The quake triggered a chemical release on the Keihin waterfront, tracked as a related event.',
  thresholdDescription:
    'Seismic intensity upper 6 or above at defense critical infrastructure',
  resourcesCommitted: `${JAPAN_EARTHQUAKE_FACILITIES.length} assets impacted or supporting; HADR and NEO lines of effort active`,
  startedAt: JAPAN_EVENT_STARTED_AT,
  lon: 139.85,
  lat: 35.55,
  threats: JAPAN_EARTHQUAKE_EVENT_THREATS,
  checklistItems: JAPAN_EARTHQUAKE_CHECKLIST_ITEMS,
  facilityIds: JAPAN_EARTHQUAKE_FACILITIES.map((facility) => facility.id),
};

export const JAPAN_CHEMICAL_EVENT: JapanOrgEventSpec = {
  demoKey: JAPAN_CHEMICAL_ORG_EVENT_DEMO_KEY,
  name: 'Chemical Release — Keihin industrial zone, Kawasaki',
  type: CHEMICAL_THREAT,
  region: 'Keihin waterfront — Kawasaki and Ota',
  summary:
    'Earthquake damage to two storage tanks at the Keihin Coastal Chemical Complex is releasing chlorine and ammonia. The plume is drifting north-east over the Haneda approaches, closing runways C and D, and the Port of Kawasaki is closed to vessel traffic. Kawasaki-ku and Ota-ku are sheltering in place.',
  thresholdDescription:
    'Hazardous chemical release affecting air and sea ports',
  resourcesCommitted: `${JAPAN_CHEMICAL_FACILITIES.length} assets in the plume or exclusion zone; hazmat and decontamination teams staged`,
  startedAt: '2026-11-02T12:58:00.000Z',
  lon: JAPAN_KEIHIN_SOURCE.lon,
  lat: JAPAN_KEIHIN_SOURCE.lat,
  threats: JAPAN_CHEMICAL_EVENT_THREATS,
  checklistItems: JAPAN_CHEMICAL_CHECKLIST_ITEMS,
  facilityIds: JAPAN_CHEMICAL_FACILITIES.map((facility) => facility.id),
};

/**
 * Earthquake first: it is the event the alert opens and the chemical one hangs
 * off.
 */
export const JAPAN_ORG_EVENTS: JapanOrgEventSpec[] = [
  JAPAN_EARTHQUAKE_EVENT,
  JAPAN_CHEMICAL_EVENT,
];

export function japanChecklistItemsForEvent(event: {
  demo_key?: string | null;
}): OrgEventChecklistItemDef[] | undefined {
  return JAPAN_ORG_EVENTS.find((spec) => spec.demoKey === event.demo_key)
    ?.checklistItems;
}

export type JapanPriorityBand = 'top' | 'middle' | 'low';

export type RankedJapanAsset = {
  rank: number;
  score: number;
  band: JapanPriorityBand;
  facility: JapanFacility;
};

/** Ranks 1–3 are the top band, 4–6 the middle band, the rest low. */
export function japanPriorityBand(rank: number): JapanPriorityBand {
  if (rank <= 3) return 'top';
  if (rank <= 6) return 'middle';
  return 'low';
}

export function japanPriorityScore(
  facility: Pick<JapanFacility, 'criticalityTier' | 'impact'>
): number {
  return TIER_WEIGHT[facility.criticalityTier] * IMPACT_WEIGHT[facility.impact];
}

/**
 * Priority = criticality × impact. Ties go to the more critical tier, then
 * alphabetically, so the order is identical on every render.
 */
export function rankJapanAssets(
  facilities: JapanFacility[] = JAPAN_FACILITIES
): RankedJapanAsset[] {
  return [...facilities]
    .sort((a, b) => {
      const byScore = japanPriorityScore(b) - japanPriorityScore(a);
      if (byScore !== 0) return byScore;
      const byTier = a.criticalityTier - b.criticalityTier;
      if (byTier !== 0) return byTier;
      return a.name.localeCompare(b.name);
    })
    .map((facility, index) => ({
      rank: index + 1,
      score: japanPriorityScore(facility),
      band: japanPriorityBand(index + 1),
      facility,
    }));
}

export const JAPAN_PRIORITY_BAND_COLORS: Record<JapanPriorityBand, string> = {
  top: '#dc2626',
  middle: '#f59e0b',
  low: '#3b82f6',
};

export function formatJapanFacilityLabel(facility: {
  name: string;
  nameJa?: string;
}): string {
  return facility.nameJa
    ? `${facility.name} / ${facility.nameJa}`
    : facility.name;
}

export function japanResourceNotesMarker(facilityId: string): string {
  return `${JAPAN_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function japanFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(JAPAN_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(JAPAN_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isJapanResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(JAPAN_RESOURCE_NOTES_PREFIX);
}

export type JapanResponseResource = {
  id: string;
  name: string;
  kind: string;
  type: string;
  quantity: number;
  unit: string;
  agency: JapanAgency;
  stagedAt: string;
  lon: number;
  lat: number;
  threats: string[];
};

/** Illustrative quantities only; none reflect real holdings or sortie plans. */
export const JAPAN_RESPONSE_RESOURCES: JapanResponseResource[] = [
  {
    id: 'relief-pallets-yokosuka',
    name: 'Relief supply pallets',
    kind: 'Supplies',
    type: 'Relief supplies',
    quantity: 120,
    unit: 'pallets',
    agency: FLEET_YOKOSUKA,
    stagedAt: 'Fleet Activities Yokosuka',
    lon: 139.668,
    lat: 35.291,
    threats: [HADR_THREAT],
  },
  {
    id: 'potable-water-kanagawa',
    name: 'Potable water',
    kind: 'Supplies',
    type: 'Water',
    quantity: 40,
    unit: 'tonnes',
    agency: KANAGAWA,
    stagedAt: 'Port of Yokohama (Honmoku)',
    lon: 139.679,
    lat: 35.43,
    threats: [HADR_THREAT],
  },
  {
    id: 'c17-sorties-yokota',
    name: 'C-17 NEO sorties planned',
    kind: 'Aircraft',
    type: 'Strategic airlift',
    quantity: 6,
    unit: 'sorties',
    agency: YOKOTA_EOC,
    stagedAt: 'Yokota Air Base',
    lon: 139.351,
    lat: 35.751,
    threats: [NEO_THREAT],
  },
  {
    id: 'c130-sorties-yokota',
    name: 'C-130 NEO sorties planned',
    kind: 'Aircraft',
    type: 'Tactical airlift',
    quantity: 8,
    unit: 'sorties',
    agency: YOKOTA_EOC,
    stagedAt: 'Yokota Air Base',
    lon: 139.345,
    lat: 35.746,
    threats: [NEO_THREAT],
  },
  {
    id: 'hazmat-decon-kawasaki',
    name: 'Hazmat decontamination teams',
    kind: 'Personnel',
    type: 'Hazmat',
    quantity: 4,
    unit: 'teams',
    agency: KAWASAKI_FIRE,
    stagedAt: 'Kawasaki Municipal Hospital',
    lon: 139.711,
    lat: 35.526,
    threats: [CHEMICAL_THREAT],
  },
  {
    id: 'jsdf-helicopters',
    name: 'JSDF helicopters',
    kind: 'Aircraft',
    type: 'Rotary wing',
    quantity: 6,
    unit: 'aircraft',
    agency: JSDF,
    stagedAt: 'Naval Air Facility Atsugi',
    lon: 139.453,
    lat: 35.452,
    threats: [HADR_THREAT],
  },
  {
    id: 'red-cross-medical-teams',
    name: 'Medical relief teams',
    kind: 'Personnel',
    type: 'Medical',
    quantity: 5,
    unit: 'teams',
    agency: RED_CROSS,
    stagedAt: 'Yokohama City University Hospital',
    lon: 139.627,
    lat: 35.341,
    threats: [HADR_THREAT],
  },
];

export function formatJapanResponseResourceName(
  resource: JapanResponseResource
): string {
  return `${resource.name} — ${resource.quantity} ${resource.unit} (${formatJapanAgency(resource.agency)})`;
}

export function japanResponseResourceNotesMarker(resourceId: string): string {
  return `${JAPAN_RESPONSE_RESOURCE_NOTES_PREFIX}${resourceId}`;
}

export function isJapanResponseResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(JAPAN_RESPONSE_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromFacility(facility: JapanFacility): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: formatJapanFacilityLabel(facility),
    risk: facility.risk,
    location: [facility.lon, facility.lat],
    operationalStatus: facility.operationalStatus,
  };
}

export function getJapanRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'Tokyo Bay — M7.3 earthquake and Keihin chemical release',
    description:
      'A M7.3 earthquake and a chemical release from the Kawasaki waterfront have impacted the following defense, transport and medical assets, listed in priority order (criticality × impact):',
    threats: rankJapanAssets().map((entry) =>
      buildAssetFromFacility(entry.facility)
    ),
    responseChecklist: JAPAN_EARTHQUAKE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedJapanThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === JAPAN_THREAT_NOTIFICATION_ID;
}

export function getJapanThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: JAPAN_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: JAPAN_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: JAPAN_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'M7.3 Tokyo Bay earthquake, with a related chemical release at Kawasaki closing Haneda runways and the Port of Kawasaki. HADR and NEO responses active.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: JAPAN_THREAT_PROVENANCE,
  };
}
